import { type RendererPayload, type RendererResult, type TemplateWithData } from '@ekkogmbh/apisdk';
import { Button, Dialog, DialogActions, DialogContent, type DialogProps, DialogTitle, Skeleton } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TemplateFieldForm } from './TemplateFieldForm';
import { TemplatePreview } from './TemplatePreview';
import { StyledFormHeader } from 'src/Common/Components/Forms/StyledFormHeader';
import { CenteredColumn } from 'src/Common/Components/Stacks';

interface Props {
  template: TemplateWithData;
  onClose: () => void;
  renderTemplate: (payload: RendererPayload, onAccept?: (result: RendererResult) => void) => void;
  isRendering?: boolean;
  dialogProps: DialogProps;
}

export const TemplatePreviewDialog = ({
  template,
  renderTemplate,
  onClose,
  dialogProps,
  isRendering,
}: Props): React.JSX.Element => {
  const renderTimeoutRef = useRef<number>();

  const [rendering, setRendering] = useState<RendererResult>();
  const [fields, setFields] = useState<Record<string, string>>({});
  const [annotations, setAnnotations] = useState<Record<string, string>>({});

  /** when changed, we want to trigger a rendering */
  const [isChanged, setChanged] = useState<boolean>(true);

  const renderCallback = useCallback(() => {
    renderTimeoutRef.current = window.setTimeout(
      () => {
        renderTemplate(
          {
            data: template.data,
            type: template.type,
            fields,
            annotations,
            autoFill: true,
            render: true,
          },
          (result) => {
            setRendering(result);

            /** result may have added new values */
            setFields(result.fields);
            setAnnotations(result.annotations);
            setChanged(false);
          },
        );
      },
      rendering ? 600 : 0,
    );
  }, [template, rendering, fields, annotations, renderTemplate]);

  useEffect(() => {
    if (!isChanged) {
      return;
    }

    renderCallback();
    return () => {
      window.clearTimeout(renderTimeoutRef.current);
    };
  }, [isChanged, renderCallback]);

  return (
    <Dialog onClose={onClose} {...dialogProps}>
      <DialogTitle id="template-preview-title">{template.name}</DialogTitle>
      <DialogContent>
        <CenteredColumn width={'100%'} justifyContent={'center'}>
          <CenteredColumn>
            {rendering ? (
              <TemplatePreview template={template} preRender={rendering} />
            ) : (
              isRendering && <Skeleton height={'25vh'} />
            )}
          </CenteredColumn>
          {Object.keys(fields).length > 0 && (
            <>
              <StyledFormHeader label={'Fields'} />
              <TemplateFieldForm
                fields={fields}
                disabled={isRendering}
                onChange={(key, value) => {
                  const newFields = { ...fields, [key]: value };
                  setFields(newFields);
                  setChanged(true);
                }}
              />
            </>
          )}

          {Object.keys(annotations).length > 0 && (
            <>
              <StyledFormHeader label={'Annotations'} />
              <TemplateFieldForm
                fields={annotations}
                disabled={isRendering}
                onChange={(key, value) => {
                  const newAnnotations = { ...annotations, [key]: value };
                  setAnnotations(newAnnotations);
                  setChanged(true);
                }}
              />
            </>
          )}
        </CenteredColumn>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant={'contained'} color={'primary'} autoFocus>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
